/**
 * Meta Pixel + Conversions API helper.
 *
 * Loads `/api/meta/config` once and stores the Pixel ID. If a Pixel ID is
 * configured server-side, the `fbevents.js` script is injected and PageView
 * fires on every route change. All track* helpers are no-ops when disabled —
 * so the rest of the app can call them safely without env-checks.
 *
 * All events (browser pixel + CAPI mirror) are additionally gated by the
 * user's marketing-cookie consent stored in localStorage.
 */
import api from "./api";

const CONSENT_KEY = "opik_cookie_consent_v1";
const _hasMarketingConsent = () => {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(CONSENT_KEY) : null;
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    return !!parsed?.marketing;
  } catch (e) { return false; }
};

let _state = {
  pixelId: null,
  injected: false,
  ready: false,
  loading: null, // promise dedupe
};

const FB_SCRIPT_ID = "meta-pixel-fbevents";

const _injectScript = (pixelId) => {
  if (_state.injected || typeof window === "undefined" || typeof document === "undefined") return;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){
    if(f.fbq) return; n=f.fbq=function(){ n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
    if(!f._fbq) f._fbq=n; n.push=n; n.loaded=!0; n.version='2.0'; n.queue=[];
    t=b.createElement(e); t.async=!0; t.id=FB_SCRIPT_ID; t.src=v;
    s=b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t,s)
  }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
  _state.injected = true;
  _state.ready = true;
};

export const initMetaPixel = async () => {
  if (_state.ready) return _state.pixelId;
  if (_state.loading) return _state.loading;
  _state.loading = api.get("/meta/config")
    .then(({ data }) => {
      if (data?.enabled && data?.pixel_id) {
        _state.pixelId = data.pixel_id;
        _injectScript(data.pixel_id);
      }
      return _state.pixelId;
    })
    .catch(() => null)
    .finally(() => { _state.loading = null; });
  return _state.loading;
};

const _generateEventId = (name) => `${name}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const _readCookie = (name) => {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") + "=([^;]*)"));
  return match ? decodeURIComponent(match[1]) : null;
};

const _send = (eventName, params = {}, capiPayload = {}) => {
  if (!_hasMarketingConsent()) return;
  const eventId = _generateEventId(eventName);
  // Client-side pixel
  try {
    if (typeof window !== "undefined" && window.fbq && _state.ready) {
      window.fbq("track", eventName, params, { eventID: eventId });
    }
  } catch (e) { /* no-op */ }
  // Server-side mirror via CAPI (best-effort, completely silent on failure)
  try {
    api.post("/meta/conversions", {
      event_name: eventName,
      event_id: eventId,
      event_source_url: typeof window !== "undefined" ? window.location.href : null,
      user_data: {
        fbp: _readCookie("_fbp"),
        fbc: _readCookie("_fbc"),
        client_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        ...capiPayload.user_data,
      },
      custom_data: {
        currency: params.currency || "USD",
        value: params.value,
        content_ids: params.content_ids,
        content_type: params.content_type || "product",
        content_name: params.content_name,
        num_items: params.num_items,
        ...capiPayload.custom_data,
      },
    }).catch(() => {});
  } catch (e) { /* no-op */ }
};

export const trackPageView = () => {
  if (!_state.ready || !_hasMarketingConsent()) return;
  try { window.fbq("track", "PageView"); } catch (e) { /* no-op */ }
};

export const trackViewContent = (gem, valueUsd) => {
  if (!gem) return;
  _send("ViewContent", {
    content_ids: [gem.id],
    content_name: gem.name,
    content_type: "product",
    value: Number(valueUsd ?? gem.reserve_price ?? 0),
    currency: "USD",
  });
};

export const trackAddToCart = (gem, valueUsd) => {
  if (!gem) return;
  _send("AddToCart", {
    content_ids: [gem.id],
    content_name: gem.name,
    content_type: "product",
    value: Number(valueUsd ?? gem.reserve_price ?? 0),
    currency: "USD",
    num_items: 1,
  });
};

export const trackInitiateCheckout = (gem, valueUsd, method) => {
  if (!gem) return;
  _send("InitiateCheckout", {
    content_ids: [gem.id],
    content_name: gem.name,
    content_type: "product",
    value: Number(valueUsd ?? gem.reserve_price ?? 0),
    currency: "USD",
    num_items: 1,
  }, { custom_data: { payment_method: method || "unknown" } });
};

export const trackPurchase = ({ gemId, gemName, value, currency = "USD", email, phone, orderId }) => {
  _send("Purchase", {
    content_ids: gemId ? [gemId] : undefined,
    content_name: gemName,
    content_type: "product",
    value: Number(value || 0),
    currency,
    num_items: 1,
  }, {
    user_data: { email, phone, external_id: orderId },
    custom_data: { order_id: orderId },
  });
};
